import React, { useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { ENV } from '../../../config/env';
import toast from 'react-hot-toast';

interface KeywordResult {
  keyword: string;
  searchVolume: number;
  difficulty: number;
  cpc: number;
  intent: 'informational' | 'commercial' | 'transactional' | 'navigational';
  trend: 'rising' | 'stable' | 'declining';
  relatedKeywords: string[];
}

const KeywordResearchTab: React.FC = () => {
  const { user, getToken } = useAuth();
  const [loading, setLoading] = useState(false);
  const [seedKeyword, setSeedKeyword] = useState('');
  const [industry, setIndustry] = useState('General');
  const [results, setResults] = useState<KeywordResult[]>([]);

  const handleResearch = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!seedKeyword.trim()) {
      toast.error('Please enter a seed keyword');
      return;
    }

    try {
      setLoading(true);
      toast.loading('Researching keywords...');

      const userId = await getToken();
      const response = await fetch(`${ENV.API_URL}/api/marketing/keyword-research`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, seedKeyword, industry })
      });

      if (!response.ok) throw new Error('Failed to research keywords');

      const data = await response.json();
      toast.dismiss();
      setResults(data.keywords);
      toast.success(`Found ${data.keywords.length} keyword opportunities!`);
    } catch (error) {
      toast.dismiss();
      console.error('Error:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to research keywords');
    } finally {
      setLoading(false);
    }
  };

  const getDifficultyColor = (difficulty: number) => {
    if (difficulty < 30) return 'text-green-600 bg-green-50';
    if (difficulty < 60) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  const getTrendIcon = (trend: string) => {
    if (trend === 'rising') return '📈';
    if (trend === 'declining') return '📉';
    return '➡️';
  };

  return (
    <div>
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Keyword Research</h3>
        <p className="text-gray-600 text-sm">
          Discover high-potential keywords with search volume, difficulty scores, and intent analysis.
        </p>
      </div>

      <form onSubmit={handleResearch} className="mb-8 space-y-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-grow">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Seed Keyword
            </label>
            <input
              type="text"
              value={seedKeyword}
              onChange={(e) => setSeedKeyword(e.target.value)}
              placeholder="e.g., content marketing"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            />
          </div>
          <div className="sm:w-48">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Industry
            </label>
            <select
              value={industry}
              onChange={(e) => setIndustry(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            >
              <option value="General">General</option>
              <option value="Technology">Technology</option>
              <option value="E-commerce">E-commerce</option>
              <option value="Health">Health</option>
              <option value="Finance">Finance</option>
              <option value="Education">Education</option>
            </select>
          </div>
        </div>

        <button
          type="submit"
          disabled={loading}
          className={`px-6 py-3 rounded-lg font-medium text-white transition ${
            loading ? 'bg-green-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'
          }`}
        >
          {loading ? 'Researching...' : 'Research Keywords'}
        </button>
      </form>

      {results.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-lg font-semibold text-gray-900">
              {results.length} Keyword Opportunities
            </h4>
            <button
              onClick={() => {
                const csv = results.map(k => 
                  `"${k.keyword}",${k.searchVolume},${k.difficulty},${k.cpc},"${k.intent}","${k.trend}"`
                ).join('\n');
                const blob = new Blob([`Keyword,Volume,Difficulty,CPC,Intent,Trend\n${csv}`], { type: 'text/csv' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `keyword-research-${Date.now()}.csv`;
                a.click();
                toast.success('CSV exported!');
              }}
              className="text-sm text-green-600 hover:text-green-700 font-medium"
            >
              Export CSV
            </button>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Keyword</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Volume</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Difficulty</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">CPC</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Intent</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Trend</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {results.map((result, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="font-medium text-gray-900">{result.keyword}</div>
                        {result.relatedKeywords.length > 0 && (
                          <div className="text-xs text-gray-500 mt-1">
                            Related: {result.relatedKeywords.slice(0, 3).join(', ')}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {result.searchVolume.toLocaleString()}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(result.difficulty)}`}>
                          {result.difficulty}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        ${result.cpc.toFixed(2)}
                      </td>
                      <td className="px-6 py-4">
                        <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">
                          {result.intent}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-lg">
                        {getTrendIcon(result.trend)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {!loading && results.length === 0 && (
        <div className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No keywords researched yet</h3>
          <p className="mt-1 text-sm text-gray-500">Enter a seed keyword above to get started</p>
        </div>
      )}
    </div>
  );
};

export default KeywordResearchTab;
